import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useConfigStore } from '@packages/stores';

import { ROUTE_PATHS, getCurrentPathname } from '@routes/router';
import { useQuoteStore } from '@store';
import { EMAIL_QUOTE_FLOW_TYPE } from '@types';
import { sendAnalyticsEvent } from '@utils';

import { useEmailQuote } from './useEmailQuote/useEmailQuote';

const activityEvents = ['mousemove', 'click', 'keypress', 'scroll', 'touchstart', 'mousedown'];

export const useEmailQuoteReminder = () => {
  const idleTimerRef = useRef<number>(); // Renamed for clarity
  const currentPathname = getCurrentPathname();
  const { myQuote } = ROUTE_PATHS();
  const { isEmailQuoteVisible, openEmailQuoteModal } = useEmailQuote();
  const didEmailQuoteShow = useQuoteStore(state => state.didEmailQuoteShow);
  const setDidEmailQuoteShow = useQuoteStore(state => state.setDidEmailQuoteShow);
  const setEmailQuoteLaunchedFrom = useQuoteStore(state => state.setEmailQuoteLaunchedFrom);
  const [showModalTrigger, setShowModalTrigger] = useState(false);

  const idleTimeoutSeconds =
    useConfigStore(state => state?.regionSpecificConfig?.RULES.IDLE_EMAIL_QUOTE_REMINDER_SECONDS) ??
    30;

  const canShowEmailQuote = useMemo(
    () => isEmailQuoteVisible && !didEmailQuoteShow && currentPathname === myQuote,
    [isEmailQuoteVisible, didEmailQuoteShow, currentPathname, myQuote],
  );

  const resetIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      window.clearTimeout(idleTimerRef.current);
    }

    idleTimerRef.current = window.setTimeout(() => {
      setShowModalTrigger(true);
    }, idleTimeoutSeconds * 1000);
  }, [idleTimeoutSeconds]);

  useEffect(() => {
    if (showModalTrigger && canShowEmailQuote) {
      setDidEmailQuoteShow(true);
      setShowModalTrigger(false);
      setEmailQuoteLaunchedFrom(EMAIL_QUOTE_FLOW_TYPE.ABANDON_CART);
      openEmailQuoteModal('EmailQuote', false, EMAIL_QUOTE_FLOW_TYPE.ABANDON_CART);
      sendAnalyticsEvent('Email a Quote Form Opened', {
        flow: EMAIL_QUOTE_FLOW_TYPE.ABANDON_CART,
      });
    } else if (showModalTrigger && !canShowEmailQuote) {
      setShowModalTrigger(false);
    }
  }, [
    showModalTrigger,
    canShowEmailQuote,
    openEmailQuoteModal,
    setEmailQuoteLaunchedFrom,
    setDidEmailQuoteShow,
  ]);

  useEffect(() => {
    if (canShowEmailQuote) {
      resetIdleTimer();

      activityEvents.forEach(event => {
        window.addEventListener(event, resetIdleTimer, { passive: true });
      });

      return () => {
        if (idleTimerRef.current) {
          window.clearTimeout(idleTimerRef.current);
        }
        activityEvents.forEach(event => {
          window.removeEventListener(event, resetIdleTimer);
        });
      };
    } else {
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
      }
    }
  }, [canShowEmailQuote, resetIdleTimer]);
};
